import { useEffect, useMemo, useState } from "react";
import { MessageSquare, CheckCircle2 } from "lucide-react";
import { useStylePanel } from "./style-panel-context";
import type { PageCommentRecord } from "@/hooks/use-page-comments";
import { Skeleton, SkeletonCard } from "@/components/skeleton";

interface ThreadedPageComment extends PageCommentRecord {
  replies: ThreadedPageComment[];
}

function matchesFilter(
  comment: PageCommentRecord,
  filter: "all" | "open" | "resolved",
) {
  if (filter === "open") {
    return !comment.resolvedAt;
  }

  if (filter === "resolved") {
    return Boolean(comment.resolvedAt);
  }

  return true;
}

function compareCreatedAtAscending(a: PageCommentRecord, b: PageCommentRecord) {
  return Date.parse(a.createdAt) - Date.parse(b.createdAt);
}

function compareCreatedAtDescending(a: PageCommentRecord, b: PageCommentRecord) {
  return Date.parse(b.createdAt) - Date.parse(a.createdAt);
}

function buildVisibleThreads(
  comments: PageCommentRecord[],
  filter: "all" | "open" | "resolved",
): ThreadedPageComment[] {
  const repliesByParent = new Map<string | null, PageCommentRecord[]>();

  for (const comment of comments) {
    const bucket = repliesByParent.get(comment.parentId) ?? [];
    bucket.push(comment);
    repliesByParent.set(comment.parentId, bucket);
  }

  for (const [parentId, bucket] of repliesByParent.entries()) {
    repliesByParent.set(
      parentId,
      [...bucket].sort(parentId ? compareCreatedAtAscending : compareCreatedAtDescending),
    );
  }

  const visit = (parentId: string | null): ThreadedPageComment[] =>
    (repliesByParent.get(parentId) ?? [])
      .map((comment) => {
        const replies = visit(comment.id);
        if (!matchesFilter(comment, filter) && replies.length === 0) {
          return null;
        }

        return {
          ...comment,
          replies,
        } satisfies ThreadedPageComment;
      })
      .filter((comment): comment is ThreadedPageComment => comment !== null);

  return visit(null);
}

function formatCommentTimestamp(value: string) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
}

interface PageCommentPanelProps {
  pageId?: string;
  pageVersionId?: string | null;
  comments: PageCommentRecord[];
  isLoading: boolean;
  error: string | null;
  createComment: (input: {
    anchorType: "page" | "component";
    componentId?: string | null;
    componentType?: string | null;
    content: string;
    pageVersionId?: string | null;
  }) => Promise<PageCommentRecord>;
  markResolved: (commentId: string) => Promise<PageCommentRecord>;
  replyToComment: (
    commentId: string,
    input: { content: string },
  ) => Promise<PageCommentRecord>;
  onSelectComment: (input: {
    anchorType: "page" | "component";
    componentId: string | null;
    componentType: string | null;
    commentId: string;
  }) => void;
  commentMode: boolean;
  onCommentModeChange: (enabled: boolean) => void;
}

export function PageCommentPanel({
  pageId,
  pageVersionId,
  comments,
  isLoading,
  error,
  createComment,
  markResolved,
  replyToComment,
  onSelectComment,
  commentMode,
  onCommentModeChange,
}: PageCommentPanelProps) {
  const {
    selectedComponentId,
    selectedComponentType,
    setSelectedComponentId,
  } = useStylePanel();
  const [content, setContent] = useState("");
  const [anchorType, setAnchorType] = useState<"page" | "component">(
    selectedComponentId ? "component" : "page",
  );
  const [filter, setFilter] = useState<"all" | "open" | "resolved">("all");
  const [isSaving, setIsSaving] = useState(false);
  const [isResolvingAll, setIsResolvingAll] = useState(false);
  const [replyTargetId, setReplyTargetId] = useState<string | null>(null);
  const [replyContent, setReplyContent] = useState("");
  const [isReplying, setIsReplying] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);

  useEffect(() => {
    setAnchorType(selectedComponentId ? "component" : "page");
  }, [selectedComponentId]);

  const openComments = useMemo(
    () => comments.filter((comment) => !comment.resolvedAt),
    [comments],
  );

  const visibleThreads = useMemo(
    () => buildVisibleThreads(comments, filter),
    [comments, filter],
  );

  const handleCreateComment = async () => {
    if (!pageId || !content.trim()) {
      return;
    }

    setIsSaving(true);
    setActionError(null);

    try {
      await createComment({
        pageVersionId,
        anchorType,
        componentId: anchorType === "component" ? selectedComponentId : null,
        componentType: anchorType === "component" ? selectedComponentType : null,
        content,
      });
      setContent("");
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to add comment",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleReply = async (commentId: string) => {
    if (!replyContent.trim()) {
      return;
    }

    setIsReplying(true);
    setActionError(null);

    try {
      await replyToComment(commentId, {
        content: replyContent,
      });
      setReplyTargetId(null);
      setReplyContent("");
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to reply to comment",
      );
    } finally {
      setIsReplying(false);
    }
  };

  const handleResolve = async (commentId: string) => {
    try {
      setActionError(null);
      await markResolved(commentId);
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to resolve comment",
      );
    }
  };

  const handleResolveAll = async () => {
    if (openComments.length === 0 || isResolvingAll) {
      return;
    }

    const confirmed = globalThis.confirm?.(
      `Resolve all ${openComments.length} open comments? This will mark every open comment thread as resolved.`,
    ) ?? true;

    if (!confirmed) {
      return;
    }

    setIsResolvingAll(true);
    setActionError(null);

    try {
      const results = await Promise.allSettled(
        openComments.map((comment) => markResolved(comment.id)),
      );
      const failedCount = results.filter((result) => result.status === "rejected").length;
      if (failedCount > 0) {
        throw new Error(
          failedCount === openComments.length
            ? "Failed to resolve comments"
            : `${failedCount} comment${failedCount === 1 ? "" : "s"} failed to resolve`,
        );
      }
    } catch (error) {
      setActionError(
        error instanceof Error ? error.message : "Failed to resolve comments",
      );
    } finally {
      setIsResolvingAll(false);
    }
  };

  const handleSelectComment = (comment: PageCommentRecord) => {
    setSelectedComponentId(comment.componentId);
    onSelectComment({
      anchorType: comment.anchorType,
      componentId: comment.componentId,
      componentType: comment.componentType,
      commentId: comment.id,
    });
  };

  const renderCommentThread = (
    comment: ThreadedPageComment,
    depth = 0,
  ) => (
    <div
      key={comment.id}
      className={`stagger-item rounded-lg border border-border bg-background p-3 ${
        depth > 0 ? "ml-4 border-l-4 border-l-[var(--color-primary)]/20" : ""
      }`}
      data-comment-id={comment.id}
    >
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => handleSelectComment(comment)}
          className="min-w-0 flex-1 text-left"
        >
          <p className="truncate text-sm font-medium text-foreground">
            {comment.authorName}
          </p>
          <p className="mt-0.5 text-[11px] text-muted-foreground">
            {comment.anchorType === "page"
              ? "Page comment"
              : `${comment.componentType ?? "Component"} [${comment.componentId}]`}
            {" · "}
            {formatCommentTimestamp(comment.createdAt)}
          </p>
        </button>
        {comment.resolvedAt ? (
          <span className="inline-flex shrink-0 items-center gap-1 rounded-full bg-[var(--color-success)]/10 px-2 py-0.5 text-[11px] font-medium text-[var(--color-success)]">
            <CheckCircle2 className="h-3 w-3" />
            Resolved
          </span>
        ) : (
          <button
            type="button"
            onClick={() => void handleResolve(comment.id)}
            className="shrink-0 text-xs font-medium text-[var(--color-primary)]"
          >
            Resolve
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => handleSelectComment(comment)}
        className="mt-2 block w-full whitespace-pre-wrap text-left text-sm text-foreground"
      >
        {comment.content}
      </button>

      <div className="mt-3 flex items-center gap-3">
        {!comment.resolvedAt ? (
          <button
            type="button"
            onClick={() => {
              setReplyTargetId((current) => current === comment.id ? null : comment.id);
              setReplyContent("");
              setActionError(null);
            }}
            className="text-xs font-medium text-muted-foreground transition hover:text-foreground"
          >
            {replyTargetId === comment.id ? "Cancel reply" : "Reply"}
          </button>
        ) : null}
        {comment.authorEmail ? (
          <span className="text-[11px] text-muted-foreground">
            {comment.authorEmail}
          </span>
        ) : null}
      </div>

      {replyTargetId === comment.id ? (
        <div className="mt-3 rounded-md border border-border bg-[var(--color-muted)]/20 p-3">
          <textarea
            value={replyContent}
            onChange={(event) => setReplyContent(event.target.value)}
            placeholder="Reply inline to this comment..."
            rows={3}
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => {
                setReplyTargetId(null);
                setReplyContent("");
              }}
              className="rounded-md bg-[var(--color-muted)] px-3 py-1.5 text-xs font-medium text-muted-foreground"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => void handleReply(comment.id)}
              disabled={isReplying || !replyContent.trim()}
              className="rounded-md bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white disabled:opacity-60"
            >
              {isReplying ? "Replying..." : "Reply"}
            </button>
          </div>
        </div>
      ) : null}

      {comment.replies.length > 0 ? (
        <div className="mt-3 space-y-3">
          {comment.replies.map((reply) => renderCommentThread(reply, depth + 1))}
        </div>
      ) : null}
    </div>
  );

  if (!pageId) {
    return (
      <div className="border-t border-border p-4">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessageSquare className="h-4 w-4" />
          Review Comments
        </h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Save the page first so comments can anchor to a real persisted page version.
        </p>
      </div>
    );
  }

  return (
    <div className="border-t border-border p-4">
      <div className="flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground">
          <MessageSquare className="h-4 w-4" />
          Review Comments
        </h3>
        <div className="flex items-center gap-2">
          {openComments.length > 0 ? (
            <button
              type="button"
              onClick={() => void handleResolveAll()}
              disabled={isResolvingAll}
              className="rounded-md border border-border bg-background px-2.5 py-1 text-xs font-medium text-muted-foreground transition hover:bg-accent hover:text-foreground disabled:opacity-60"
            >
              {isResolvingAll ? "Resolving all..." : "Resolve all"}
            </button>
          ) : null}
          <span className="rounded-full bg-[var(--color-primary)]/10 px-2 py-0.5 text-xs font-medium text-[var(--color-primary)]">
            {openComments.length} open
          </span>
        </div>
      </div>

      <p className="mt-2 text-xs leading-5 text-muted-foreground">
        Anchored to
        {" "}
        {anchorType === "component" && selectedComponentId
          ? `${selectedComponentType ?? "component"} [${selectedComponentId}]`
          : "the page"}
        .
      </p>

      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={() => setAnchorType("component")}
          disabled={!selectedComponentId}
          className={`rounded-md px-2 py-1 text-xs ${
            anchorType === "component"
              ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
              : "bg-[var(--color-muted)] text-muted-foreground"
          } disabled:cursor-not-allowed disabled:opacity-40`}
        >
          Component
        </button>
        <button
          type="button"
          onClick={() => setAnchorType("page")}
          className={`rounded-md px-2 py-1 text-xs ${
            anchorType === "page"
              ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
              : "bg-[var(--color-muted)] text-muted-foreground"
          }`}
        >
          Page
        </button>
      </div>

      <div className="mt-3 flex gap-2">
        {(["all", "open", "resolved"] as const).map((value) => (
          <button
            key={value}
            type="button"
            onClick={() => setFilter(value)}
            className={`rounded-md px-2 py-1 text-xs ${
              filter === value
                ? "bg-[var(--color-primary)]/10 text-[var(--color-primary)]"
                : "bg-[var(--color-muted)] text-muted-foreground"
            }`}
          >
            {value === "all" ? "All" : value === "open" ? "Open" : "Resolved"}
          </button>
        ))}
        <button
          type="button"
          onClick={() => onCommentModeChange(!commentMode)}
          className={`ml-auto rounded-md px-2 py-1 text-xs ${
            commentMode
              ? "bg-amber-500/15 text-amber-900 dark:text-amber-100"
              : "bg-[var(--color-muted)] text-muted-foreground"
          }`}
        >
          {commentMode ? "Comment mode on" : "Comment mode off"}
        </button>
      </div>

      <textarea
        value={content}
        onChange={(event) => setContent(event.target.value)}
        placeholder="Add a persistent review comment..."
        rows={4}
        className="mt-3 w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground outline-none"
      />

      {error || actionError ? (
        <p className="mt-2 text-xs text-[var(--color-destructive)]">
          {actionError ?? error}
        </p>
      ) : null}

      <button
        type="button"
        onClick={() => void handleCreateComment()}
        disabled={isSaving || !content.trim()}
        className="mt-3 w-full rounded-md bg-[var(--color-primary)] px-3 py-2 text-sm font-medium text-white disabled:opacity-60"
      >
        {isSaving ? "Saving comment..." : "Add comment"}
      </button>

      <div className="mt-4 space-y-3">
        {isLoading ? (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-4 w-16" />
            </div>
            {Array.from({ length: 2 }).map((_, index) => (
              <SkeletonCard key={index} className="border-border bg-background p-3" />
            ))}
          </div>
        ) : visibleThreads.length === 0 ? (
          <p className="text-sm text-muted-foreground">
            No persistent comments match this filter yet.
          </p>
        ) : (
          visibleThreads.map((comment) => renderCommentThread(comment))
        )}
      </div>
    </div>
  );
}
